import React, { FC } from 'react';
import {
  Box,
  Button,
  Dialog,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

interface LockedByPropertyModalProps {
  open: boolean;
  onClose: () => void;
  onContinue?: () => void;
  propertyEmail: string;
  propertyName?: string;
}

const LockedByPropertyModal: FC<LockedByPropertyModalProps> = ({
  open,
  onClose,
  onContinue,
  propertyEmail,
  propertyName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const content = (
    <Stack gap={3} sx={{ p: 3 }}>
      {/* Icon + title */}
      <Stack direction="row" alignItems="center" gap={1.5}>
        <LockIcon
          sx={{ color: 'text.secondary', fontSize: 28, flexShrink: 0 }}
        />
        <Typography variant="h6" fontWeight={700}>
          File Locked by Property
        </Typography>
      </Stack>

      {/* Body */}
      <Stack gap={1.5}>
        <Typography variant="body2" color="text.primary">
          The file has been locked by{' '}
          {propertyName ? <strong>{propertyName}</strong> : 'the property'} for
          this stage of review. If you need to make a change, please contact the
          property at{' '}
          <Box
            component="a"
            href={`mailto:${propertyEmail}`}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              wordBreak: 'break-all',
            }}
          >
            {propertyEmail}
          </Box>
          .
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Tap <strong>Continue</strong> to see a read-only version of the
          information provided so far.
        </Typography>
      </Stack>

      {/* Actions */}
      <Stack direction="row" justifyContent="flex-end" gap={1.5}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button variant="contained" onClick={onContinue ?? onClose}>
          Continue
        </Button>
      </Stack>
    </Stack>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { borderRadius: '16px 16px 0 0', maxHeight: '85vh' },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {content}
    </Dialog>
  );
};

export default LockedByPropertyModal;
